import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsApi, tasksApi, usersApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Plus, Calendar, Clock, Users, User, Edit2,
  AlertTriangle, HandHelping, CheckSquare, Save, Trash2, FileQuestion,
  LayoutGrid, GanttChart, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import GanttTimeline from '../components/GanttTimeline';
import TaskEditDialog from '../components/TaskEditDialog';
import ActivityLog from '../components/ActivityLog';
import Comments from '../components/Comments';
import Attachments from '../components/Attachments';

const columns = [
  { id: 'todo', label: 'To Do', color: '#1A2B5E' },
  { id: 'in_progress', label: 'In Progress', color: '#106EFD' },
  { id: 'review', label: 'In Review', color: '#E171EA' },
  { id: 'done', label: 'Complete', color: '#37C563' },
];

const statusConfig = columns.map(c => ({ ...c, value: c.id }));

const priorityStyle = (p) => p === 'high' ? 'bg-red-100 text-red-700' : p === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700';

const getDeadlineState = (d) => {
  if (!d) return 'none';
  const h = (new Date(d) - new Date()) / 36e5;
  return h < 0 ? 'overdue' : h < 48 ? 'urgent' : h < 120 ? 'approaching' : 'safe';
};

const deadlineRing = { overdue: 'ring-2 ring-red-400 bg-red-50/50', urgent: 'ring-2 ring-orange-400', approaching: 'ring-1 ring-yellow-300', safe: '', none: '' };

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editProject, setEditProject] = useState({});
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '', description: '', priority: 'medium', status: 'todo',
    assignee_id: '', start_date: '', due_date: '', estimated_hours: 0, request_type: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [projRes, usersRes] = await Promise.all([projectsApi.getById(id), usersApi.getAll()]);
      setProject(projRes.data);
      setTasks(projRes.data.tasks || []);
      setUsers(usersRes.data);
      setEditProject(projRes.data);
    } catch {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveProject = async () => {
    try {
      const { tasks: _, task_count, tasks_done, id: _id, created_by, created_at, updated_at, ...data } = editProject;
      await projectsApi.update(id, data);
      setProject({ ...project, ...data });
      setEditMode(false);
      toast.success('Project updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await projectsApi.update(id, { status: newStatus });
      setProject({ ...project, status: newStatus });
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await tasksApi.create({ ...newTask, project_id: id });
      toast.success('Task added to project');
      setTaskDialogOpen(false);
      setNewTask({ title: '', description: '', priority: 'medium', status: 'todo', assignee_id: '', start_date: '', due_date: '', estimated_hours: 0, request_type: '' });
      fetchData();
    } catch { toast.error('Failed to create task'); }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await tasksApi.update(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      toast.success(`Moved to ${columns.find(c => c.id === newStatus)?.label}`);
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Delete this project? Tasks will be unlinked but not deleted.')) return;
    try {
      await projectsApi.delete(id);
      toast.success('Project deleted');
      navigate('/projects');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;
  if (!project) return null;

  const sc = statusConfig.find(s => s.value === project.status) || statusConfig[0];
  const progress = tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'done').length / tasks.length) * 100) : 0;
  const deadline = getDeadlineState(project.due_date);
  const assignees = users.filter(u => (project.assignee_ids || []).includes(u.id));

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-to-projects-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']" data-testid="project-title">{project.title}</h1>
          {project.description && <p className="text-navy/60 mt-1">{project.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={project.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[160px]" data-testid="project-status-select">
              <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: sc.color }} /><SelectValue /></div>
            </SelectTrigger>
            <SelectContent>
              {statusConfig.map(s => <SelectItem key={s.value} value={s.value}><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />{s.label}</div></SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => { setEditMode(!editMode); setEditProject(project); }} data-testid="edit-project-btn"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
          <Button variant="outline" className="text-red-500 hover:bg-red-50" onClick={handleDeleteProject} data-testid="delete-project-btn"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Edit form */}
      {editMode && (
        <Card className="border-[#40CFDE]/30">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2"><Label>Title</Label><Input value={editProject.title || ''} onChange={e => setEditProject({...editProject, title: e.target.value})} /></div>
              <div className="space-y-2 col-span-2"><Label>Description</Label><Textarea value={editProject.description || ''} onChange={e => setEditProject({...editProject, description: e.target.value})} rows={2} /></div>
              <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={editProject.start_date || ''} onChange={e => setEditProject({...editProject, start_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={editProject.due_date || ''} onChange={e => setEditProject({...editProject, due_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>Priority</Label>
                <Select value={editProject.priority || 'medium'} onValueChange={v => setEditProject({...editProject, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                  <SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem>
                </SelectContent></Select></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveProject} className="bg-[#40CFDE] text-[#1A2B5E]"><Save className="w-4 h-4 mr-1" /> Save</Button>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project info bar */}
      <div className="flex flex-wrap items-center gap-6 p-4 bg-white rounded-xl border border-navy/10">
        <Badge className={priorityStyle(project.priority)}>{project.priority} priority</Badge>
        {project.start_date && <span className="text-sm text-navy/60 flex items-center gap-1"><Calendar className="w-4 h-4" /> Start: {new Date(project.start_date).toLocaleDateString()}</span>}
        {project.due_date && <span className={`text-sm flex items-center gap-1 ${deadline === 'overdue' ? 'text-red-600 font-semibold' : deadline === 'urgent' ? 'text-orange-600' : 'text-navy/60'}`}>
          {deadline === 'overdue' && <AlertTriangle className="w-4 h-4" />}<Clock className="w-4 h-4" /> Due: {new Date(project.due_date).toLocaleDateString()}{deadline === 'overdue' && ' (Overdue!)'}
        </span>}
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-navy/40" />
          {assignees.length > 0 ? assignees.map(u => <span key={u.id} className="text-sm">{u.name}</span>) : <span className="text-sm text-navy/40">No assignees</span>}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-navy/50">{tasks.filter(t => t.status === 'done').length}/{tasks.length} tasks</span>
          <div className="w-32 h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: sc.color }} />
          </div>
          <span className="text-sm font-semibold" style={{ color: sc.color }}>{progress}%</span>
        </div>
      </div>

      {/* Task section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth']">Tasks</h2>
          <div className="flex bg-navy/5 rounded-lg p-0.5" data-testid="view-toggle">
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white shadow-sm text-[#1A2B5E]' : 'text-navy/50 hover:text-navy/70'}`}
              data-testid="view-kanban-btn"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'timeline' ? 'bg-white shadow-sm text-[#1A2B5E]' : 'text-navy/50 hover:text-navy/70'}`}
              data-testid="view-timeline-btn"
            >
              <GanttChart className="w-3.5 h-3.5" /> Timeline
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white shadow-sm text-[#1A2B5E]' : 'text-navy/50 hover:text-navy/70'}`}
              data-testid="view-calendar-btn"
            >
              <CalendarDays className="w-3.5 h-3.5" /> Calendar
            </button>
          </div>
        </div>
        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="add-task-to-project-btn"><Plus className="w-4 h-4 mr-2" /> Add Task</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-['Amaranth']">Add Task to Project</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
              <div className="space-y-2"><Label>Title *</Label><Input value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} placeholder="Task title" required data-testid="project-task-title" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={newTask.description} onChange={e => setNewTask({...newTask, description: e.target.value})} placeholder="Brief description" rows={2} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Priority</Label>
                  <Select value={newTask.priority} onValueChange={v => setNewTask({...newTask, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                    <SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem>
                  </SelectContent></Select></div>
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={newTask.status} onValueChange={v => setNewTask({...newTask, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                    {columns.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Assignee</Label>
                  <Select value={newTask.assignee_id} onValueChange={v => setNewTask({...newTask, assignee_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent></Select></div>
                <div className="space-y-2"><Label>Estimated Hours</Label><Input type="number" value={newTask.estimated_hours} onChange={e => setNewTask({...newTask, estimated_hours: parseFloat(e.target.value) || 0})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={newTask.start_date} onChange={e => setNewTask({...newTask, start_date: e.target.value})} /></div>
                <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={newTask.due_date} onChange={e => setNewTask({...newTask, due_date: e.target.value})} /></div>
              </div>
              <div className="space-y-2"><Label>Request Type</Label>
                <Select value={newTask.request_type || 'none'} onValueChange={v => setNewTask({...newTask, request_type: v === 'none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent>
                  <SelectItem value="none">None</SelectItem><SelectItem value="document">Provide Documents</SelectItem><SelectItem value="update">Provide Update</SelectItem><SelectItem value="review">Review Required</SelectItem>
                </SelectContent></Select></div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="submit-project-task-btn">Add Task</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {viewMode === 'kanban' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="project-kanban">
        {columns.map(column => {
          const colTasks = tasks.filter(t => t.status === column.id);
          return (
            <div
              key={column.id}
              className="rounded-xl p-3 min-h-[200px]"
              style={{ backgroundColor: `${column.color}08` }}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
              onDrop={e => { e.preventDefault(); const tid = e.dataTransfer.getData('text/plain'); if (tid) handleTaskStatusChange(tid, column.id); }}
              data-testid={`project-column-${column.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: column.color }} />
                  <h3 className="font-medium text-[#1A2B5E]">{column.label}</h3>
                </div>
                <Badge variant="outline" className="text-xs">{colTasks.length}</Badge>
              </div>
              <div className="space-y-2">
                {colTasks.length === 0 ? (
                  <div className="flex items-center justify-center h-16 text-navy/30 text-sm border-2 border-dashed border-navy/10 rounded-lg">Drop here</div>
                ) : colTasks.map(task => {
                  const dl = getDeadlineState(task.due_date);
                  const assignee = users.find(u => u.id === task.assignee_id);
                  return (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={e => { e.dataTransfer.setData('text/plain', task.id); e.dataTransfer.effectAllowed = 'move'; }}
                      className={`bg-white rounded-lg p-3 shadow-sm border border-navy/10 cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${deadlineRing[dl]} ${task.needs_help ? 'border-l-4 border-l-orange-400' : ''}`}
                      style={!task.needs_help ? { borderLeft: `4px solid ${column.color}` } : {}}
                      onClick={() => { setEditingTask(task); setEditTaskDialogOpen(true); }}
                      data-testid={`project-task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-[#1A2B5E] text-sm flex-1">{task.title}</h4>
                        {task.needs_help && <HandHelping className="w-4 h-4 text-orange-500 animate-pulse flex-shrink-0" />}
                      </div>
                      {task.description && <p className="text-xs text-navy/60 mt-1 line-clamp-2">{task.description}</p>}
                      {task.request_type && <Badge className="mt-1 text-[10px] bg-purple-100 text-purple-700"><FileQuestion className="w-3 h-3 mr-1" />{task.request_type}</Badge>}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={`text-xs ${priorityStyle(task.priority)}`}>{task.priority}</Badge>
                        {task.estimated_hours > 0 && <span className="text-xs text-navy/50">{task.estimated_hours}h</span>}
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-navy/5">
                        <div className="flex items-center gap-2 text-xs text-navy/50">
                          {task.start_date && <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" />{new Date(task.start_date).toLocaleDateString()}</span>}
                          {task.start_date && task.due_date && <span>-</span>}
                          {task.due_date && <span className={`flex items-center gap-0.5 ${dl === 'overdue' ? 'text-red-600 font-semibold' : dl === 'urgent' ? 'text-orange-600' : ''}`}>
                            {dl === 'overdue' && <AlertTriangle className="w-3 h-3" />}
                            {!task.start_date && <Clock className="w-3 h-3" />}
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>}
                        </div>
                        {assignee ? (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ backgroundColor: column.color }} title={assignee.name}>
                            {assignee.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                        ) : <User className="w-4 h-4 text-navy/30" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      ) : viewMode === 'timeline' ? (
        <GanttTimeline tasks={tasks} users={users} />
      ) : (
        /* Calendar mini view for project tasks */
        <div className="bg-white rounded-xl border border-navy/10 p-4" data-testid="project-calendar-view">
          <div className="space-y-2">
            {tasks.filter(t => t.due_date || t.start_date).length === 0 ? (
              <p className="text-sm text-navy/40 text-center py-8">No tasks with dates to show on calendar</p>
            ) : (
              tasks.filter(t => t.due_date || t.start_date).sort((a, b) => (a.due_date || a.start_date || '').localeCompare(b.due_date || b.start_date || '')).map(task => (
                <div key={task.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-navy/10 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => { setEditingTask(task); setEditTaskDialogOpen(true); }}>
                  <div className="w-2 h-8 rounded-full" style={{ backgroundColor: statusConfig.find(s => s.value === task.status)?.color || '#1A2B5E' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A2B5E] truncate">{task.title}</p>
                    <p className="text-xs text-navy/50">
                      {task.start_date && new Date(task.start_date).toLocaleDateString()}
                      {task.start_date && task.due_date && ' → '}
                      {task.due_date && new Date(task.due_date).toLocaleDateString()}
                      {task.recurrence && <span className="ml-2 text-[#40CFDE]">↻ {task.recurrence}</span>}
                    </p>
                  </div>
                  <Badge className={priorityStyle(task.priority) + ' text-xs'}>{task.priority}</Badge>
                </div>
              ))
            )}
          </div>
          <p className="text-xs text-navy/40 text-center mt-3">
            For full calendar view, visit <a href="/calendar" className="text-[#106EFD] hover:underline">Calendar page</a>
          </p>
        </div>
      )}

      {/* Project Attachments, Comments, Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-2">
        <div className="lg:col-span-1">
          <Attachments entityType="project" entityId={id} />
        </div>
        <div className="lg:col-span-1">
          <Comments entityType="project" entityId={id} />
        </div>
        <div className="lg:col-span-1">
          <ActivityLog entityType="project" entityId={id} />
        </div>
      </div>

      {/* Task Edit Dialog */}
      <TaskEditDialog
        task={editingTask}
        open={editTaskDialogOpen}
        onOpenChange={setEditTaskDialogOpen}
        users={users}
        onSaved={fetchData}
      />
    </div>
  );
};

export default ProjectDetail;
